/**
 * A carousel slider component using Embla Carousel
 * @see {@link https://www.embla-carousel.com/examples/predefined/#default}
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode[]} props.children - Array of child elements to display as slides
 * @param {number} props.width - Width of the carousel container in pixels
 * @returns {JSX.Element} Carousel slider component
 */

import useEmblaCarousel from 'embla-carousel-react'
import React from 'react'
import styled from 'styled-components'

import { SliderControls } from './slider-controls'

import type { ClassNameProps } from '@root/shared/lib/react'

type Props = {
  children: Array<React.ReactNode>
} & ClassNameProps

export const Slider: React.FC<Props> = ({ children, className }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true })

  return (
    <Root className={className}>
      <ViewPort ref={emblaRef}>
        <Container>
          {children.map((slide, index) => (
            <Slide key={index}>{slide}</Slide>
          ))}
        </Container>

        <SliderControls api={emblaApi!} />
      </ViewPort>
    </Root>
  )
}

const Root = styled.div`
  width: var(--slider-width, 100%);
`

const ViewPort = styled.div`
  overflow: hidden;
`

const Container = styled.div`
  display: flex;
  touch-action: pan-y pinch-zoom;
`

const Slide = styled.div`
  transform: translate3d(0, 0, 0);
  flex: 0 0 100%;
  min-width: 0;

  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`
